import { COMPONENT_PLACEMENT_TOP_ONLY } from '@rta/rta-blueprint-library';
import type { Group, Mesh, MeshStandardMaterial } from 'three';


import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { CutoutInfo as ManagerCutoutInfo } from '../../../managers/cutout-manager';
import type { CutoutInfo } from '../../../../model/cutout-info';

// Openings whose head is arched rather than square. Their cutout is built as a vertical
// profile and extruded into the panel; every other sku keeps the flat-footprint path.
const ARCH_CUTOUT_SKUS = new Set(['WOOD', 'LOG-INSERT']);

// Vents carry cutoutDepth 0 and a zero-area top footprint, so they never reach the cutout guard
// below and have never cut a hole in 3D. They are openings in the panel face, not footprints, so
// each is built from the slot layout of its 2D artwork and extruded through the panel thickness.
// The value selects that layout (see vent-openings.helpers); null draws the plain rectangle the
// no-svg vents use in 2D.
const VENT_PROFILE_BY_SKU = new Map<string, string | null>([
	['MOK-VENT-SIDE', 'mokVentsSide'],
	['MOK-VENT-BACK', 'mokVentsBack'],
	['PASSTHROUGH-VENT', 'passthroughVent'],
	['VENT-SIDE', null],
	['VENT-BACK', null]
]);

/** Clears both panel faces so the CSG subtract leaves no skin behind. */
const VENT_DEPTH_MARGIN = 0.5;

/**
 * Populate the cutout manager with cutouts based on current kitchen data.
 * Returns the collected cutouts for optional debug visualization.
 *
 * When `targetIslandIndex` is provided, only cutouts for that island are
 * collected and their `islandIndex` is remapped to `remapTo` (default 0).
 * This is used during per-island rebuilds where the island groups array
 * contains only the single rebuilt island at position 0.
 */
/**
 * Per-appliance changes applied to the cutouts collected from the domain,
 * without touching the domain. The drag-and-drop preview uses it: the lifted
 * column's cutouts are left out (the countertop closes over its old slot
 * until the drop) and the columns sliding aside carry their cutouts with them.
 * Offsets are island-local, the same frame the appliance meshes move in.
 */
export interface CutoutAdjust {
	excludeApplianceIds?: Array<string>;
	offsetsByApplianceId?: Record<string, { x: number; y?: number; z: number }>;
}

export const populateCutoutsFromKitchenDataCfn = (
	self: KitchenSceneBuilder,
	_params: Record<string, never>,
	targetIslandIndex?: number,
	remapTo = 0,
	adjust?: CutoutAdjust
): ManagerCutoutInfo[] => {
	if (!self.configAccess.cutoutManager) return [];

	const islands = self.kitchenDataManagerAccess.getIslands();

	// Reset before collecting new cutouts. For a per-island rebuild
	// (targetIslandIndex set) clear ONLY that island's cutouts so concurrent
	// rebuilds of other islands don't wipe each other's cutout data mid-flight.
	// A full/current-scene regen (no target) clears everything as before.
	if (targetIslandIndex !== undefined) {
		const targetIslandId = islands[targetIslandIndex]?.id as string | undefined;
		if (targetIslandId) {
			self.configAccess.cutoutManager.clearCutoutsForIslandId(targetIslandId);
		} else {
			self.configAccess.cutoutManager.clearCutouts();
		}
	} else {
		self.configAccess.cutoutManager.clearCutouts();
	}

	const zFighting = 0.03125;
	const excluded = new Set(adjust?.excludeApplianceIds ?? []);
	const offsets = adjust?.offsetsByApplianceId ?? {};

	for (let islandIndex = 0; islandIndex < islands.length; islandIndex++) {
		// Skip islands that aren't the target when filtering
		if (targetIslandIndex !== undefined && islandIndex !== targetIslandIndex) continue;

		const island = islands[islandIndex];
		// Remap index when targeting a specific island (single-element array)
		const outputIndex = targetIslandIndex !== undefined ? remapTo : islandIndex;

		if (island.panels) {
			for (const panel of island.panels) {
				// Use translatedCenter for consistency with geometry builder panel positioning
				const panelCenter = panel.translatedCenter || panel.center || { x: 0, y: 0, z: 0 };
				// originAngle is stored in radians from KitchenDataManager
				const rotationZ = panel.originAngle || 0;

				if (panel.applianceGroups) {
					for (const applianceGroup of panel.applianceGroups) {
						const agCenter = applianceGroup.center || { x: 0, y: 0, z: 0 };

						if (applianceGroup.items) {
							for (const item of applianceGroup.items) {
								// Spacer items reserve space but should not cut holes
								if (item.isSpacer) continue;
								// a lifted column keeps no hole until it is dropped
								if (excluded.has(item.id)) continue;
								const offset = offsets[item.id] ?? null;

								// Vent openings take the vertical-profile path: the slots of the 2D
								// artwork cut straight through the panel. Handled before the guard
								// below, which a zero cutoutDepth would otherwise fail.
								const ventSku = String((item as { sku?: string }).sku ?? '');
								if (item.categoryKey === 'vent' && VENT_PROFILE_BY_SKU.has(ventSku)
									&& item.cutoutSize && item.cutoutSize.width > 0 && item.cutoutSize.height > 0) {
									const ventX = agCenter.x;
									const ventThickness = (panel.size?.depth ?? panel.thickness ?? 1) + VENT_DEPTH_MARGIN;

									self.configAccess.cutoutManager.addLegacyCutout({
										islandIndex: outputIndex,
										islandId: island.id,
										x: panelCenter.x + (Math.cos(rotationZ) * ventX) + (offset?.x ?? 0),
										y: agCenter.y + (item.cutoutCenter?.y || 0) + (offset?.y ?? 0),
										z: -panelCenter.z + (Math.sin(rotationZ) * ventX) + (offset?.z ?? 0),
										rotationY: -rotationZ - (item.instanceRotation?.z || 0),
										vertices: [],
										width: item.cutoutSize.width,
										height: item.cutoutSize.height,
										depth: ventThickness,
										applianceId: item.id,
										ventProfile: {
											svgKey: VENT_PROFILE_BY_SKU.get(ventSku) ?? null,
											panelWidth: panel.size?.width
										}
									} as CutoutInfo);
									continue;
								}

								if (item.cutoutSize &&
									item.cutoutSize.width !== 0 &&
									item.cutoutSize.height !== 0 &&
									item.cutoutSize.depth !== 0) {

									// Extend cutout through the countertop slab for:
									// - Circular cutouts (hole category, e.g. keg taps, gas line punctures)
									// - Front-and-top items (e.g. kamado grills, grills with front overhang)
									const isCircularCutout = item.categoryKey === 'hole';
									const isFrontAndTopItem = item.distanceFrontOfFace != null && item.distanceAboveCountertop != null;
									const counterThicknessAdjustment =
										(isCircularCutout || isFrontAndTopItem) && island.counter
											? (island.counter.size?.depth || 0) + zFighting
											: 0;

									// catalog placement is tested directly because some 'front and top' SKUs leave distanceAboveCountertop null
									const counterCenterY = island.counter?.center?.y;
									const isTopOnlyCutout = (item.placement ?? '').toLowerCase().trim() === COMPONENT_PLACEMENT_TOP_ONLY
										&& (item.distanceBelowCountertop ?? 0) > 0
										&& !isCircularCutout
										&& counterCenterY != null;

									// Match legacy GenerateCutoutsCommand.hx positioning exactly
									const cutoutX = agCenter.x;
									const cutoutZ = item.cutoutCenter?.z || 0;

									// When distanceFrontOfFace < countertop overhang, the cutout ears
									// align flush with the countertop edge causing a visible sliver.
									// Extend front vertices and compensate position to fix this.
									const vertices = [...(item.corners?.top || [])];
									const overhang = panel.countertopOverhang ?? 2;
									const needsBleed = item.distanceFrontOfFace != null
										&& item.distanceFrontOfFace < overhang
										&& vertices.length > 4;
									const cutoutBleed = needsBleed ? 1 : 0;

									if (cutoutBleed > 0) {
										const maxY = Math.max(...vertices.map((v: { x: number; y: number }) => v.y));
										for (const v of vertices) {
											if (v.y >= maxY - 0.01) v.y += cutoutBleed;
										}
									}

									// Cutout center position in 3D world space
									const cX = panelCenter.x + (Math.cos(rotationZ) * cutoutX) + (Math.sin(rotationZ) * cutoutZ);
									const cY = isTopOnlyCutout
										? counterCenterY
										: agCenter.y + (item.cutoutCenter?.y || 0) - (counterThicknessAdjustment / 2) - zFighting;
									const cZ = -panelCenter.z - (Math.cos(rotationZ) * cutoutZ) + (Math.sin(rotationZ) * cutoutX) - zFighting - (cutoutBleed / 2);

									// The cutout position is already at the appliance center, so
									// no position orbit is needed — only rotationY changes.
									// The model orbits around its bbox center in load-collada,
									// which aligns with the cutout center in the XZ plane.
									const irZ = item.instanceRotation?.z || 0;

									const cutout: CutoutInfo = {
										islandIndex: outputIndex,
										// Stable identity — authoritative match key for the island group,
										// independent of scene/array ordering (which drifts after per-island rebuilds).
										islandId: island.id,
										x: cX + (offset?.x ?? 0),
										y: cY + (offset?.y ?? 0),
										z: cZ + (offset?.z ?? 0),
										rotationY: -rotationZ - irZ, // panel angle (negated) + instance rotation (negated to match)
										vertices,
										width: item.cutoutSize.width || 0,
										height: isTopOnlyCutout
											? (island.counter.size?.depth || 0) + (zFighting * 2)
											: (item.cutoutSize.height || 0) + counterThicknessAdjustment,
										depth: item.cutoutSize.depth || 0,
										// Link to appliance for synchronized transition timing
										applianceId: item.id,
										// WOOD / LOG-INSERT is an arched opening, not a rectangle
										archProfile: ARCH_CUTOUT_SKUS.has(String((item as { sku?: string }).sku ?? ''))
									};


									self.configAccess.cutoutManager.addLegacyCutout(cutout);
								}
							}
						}
					}
				}
			}
		}
	}

	// Return the manager-format cutouts (with generated ids/points) for debug viz
	return self.configAccess.cutoutManager.cutouts;
};

/**
 * Generate cutouts for appliances
 * @returns Debug visualization group (can be added to scene for debugging)
 */
export const generateCutoutsCfn = async (
	self: KitchenSceneBuilder,
	_params: Record<string, never>
): Promise<Group | null> => {
	if (!self.configAccess.cutoutManager) {
		return null;
	}

	const cutoutData = populateCutoutsFromKitchenDataCfn(self, {});

	// Perform the actual CSG cutouts (this clears the cutout list)
	await self.configAccess.cutoutManager.generateCutouts(self.islandGroupsAccess);

	// Create debug visualization AFTER cutouts are generated, using stored data
	// Debug meshes are added to a separate group, not to islands
	const debugGroup = self.configAccess.cutoutManager.createDebugVisualizationFromData(cutoutData, self.islandGroupsAccess);
	return debugGroup;
};

/**
 * Restore clean (pre-CSG) geometry inside each island so that a subsequent
 * `generateCutouts` call starts from un-holed meshes.
 *
 * During the initial build, `generateCutoutsCfn` creates hidden `*-no-cutout`
 * clones that contain the original (solid) geometry. On regeneration we:
 *   1. Promote those clones back to their primary type (`countertop`, `panel`)
 *   2. Remove the stale CSG-processed meshes (which have old holes)
 *
 * This ensures `generateCutoutsCfn` unions clean geometry and produces correct
 * `*-no-cutout` clones for the show/hide toggle.
 */
const restoreCleanGeometry = (islandGroups: Array<Group>): void => {
	const swapPairs: Array<{ noCutoutType: string; primaryType: string }> = [
		{ noCutoutType: 'countertop-no-cutout', primaryType: 'countertop' },
		{ noCutoutType: 'panel-no-cutout', primaryType: 'panel' }
	];

	for (const island of islandGroups) {
		for (const { noCutoutType, primaryType } of swapPairs) {
			// Collect matching children (iterate over a copy since we mutate)
			const children = [...island.children];

			const noCutoutMeshes = children.filter(
				(c) => (c as Mesh).userData?.type === noCutoutType
			) as Array<Mesh>;

			const primaryMeshes = children.filter(
				(c) => (c as Mesh).userData?.type === primaryType
			) as Array<Mesh>;

			if (noCutoutMeshes.length === 0) continue;

			// Remove stale CSG-processed primary meshes
			for (const stale of primaryMeshes) {
				if (stale.geometry) {
					stale.geometry.dispose();
				}
				if (stale.material) {
					const mat = stale.material;
					if (Array.isArray(mat)) {
						mat.forEach((m: { dispose?: () => void }) => m.dispose?.());
					} else {
						(mat as MeshStandardMaterial).dispose();
					}
				}
				island.remove(stale);
			}

			// Promote no-cutout clones to primary type
			for (const clone of noCutoutMeshes) {
				const oldId = clone.userData?.id as string || '';
				clone.userData = {
					...clone.userData,
					type: primaryType,
					id: oldId.replace(/-no-cutout$/, '')
				};
				clone.visible = true;
			}
		}
	}
};

/**
 * Regenerate cutouts on the current scene islands (used for incremental updates).
 * This keeps existing appliances in place and updates only structural meshes.
 *
 * When `targetIslandIndex` is provided, only cutouts for that specific island
 * (by global kitchen index) are generated. The index is remapped to 0 to match
 * the single-element `islandGroups` array used during per-island rebuilds.
 */
export const regenerateCutoutsForIslandsCfn = async (
	self: KitchenSceneBuilder,
	{ islandGroups, targetIslandIndex, adjust }: { islandGroups: Array<Group>; targetIslandIndex?: number; adjust?: CutoutAdjust }
): Promise<Group | null> => {
	if (!self.configAccess.cutoutManager) return null;
	if (!islandGroups || islandGroups.length === 0) return null;

	// Restore clean (un-CSG'd) geometry before regeneration so that
	// generateCutoutsCfn starts from solid meshes and produces correct
	// no-cutout clones for the show/hide toggle.
	restoreCleanGeometry(islandGroups);

	const cutoutData = populateCutoutsFromKitchenDataCfn(self, {}, targetIslandIndex, 0, adjust);
	await self.configAccess.cutoutManager.generateCutouts(islandGroups);

	return self.configAccess.cutoutManager.createDebugVisualizationFromData(cutoutData, islandGroups);
};
